import type { TFunction } from "i18next";
import { u2000BuildAppRouteHref } from "../../../../../u2000Core/tools/u2000OpenAppRouteInNewTab";
import { u3125FormatCurrency } from "../../../../../u3125Facture/U3125";
import { NS } from "../../../../U3121";
import type { U3121BillingSummary, U3121BillingTecPool, U3121BillingTecPoolSummary } from "../../../../U3121Service";
import U3121BillingSummaryKindBreakdown from "./U3121BillingSummaryKindBreakdown";
import U3121BillingSummaryMetricCard from "./U3121BillingSummaryMetricCard";
import U3121BillingSummaryNoAccessPanel from "./U3121BillingSummaryNoAccessPanel";
import U3121BillingSummarySubProjectBreakdown from "./U3121BillingSummarySubProjectBreakdown";

interface U3121BillingSummaryTecSectionProps {
  summary: U3121BillingSummary;
  onOpenDetail: (pool: U3121BillingTecPool) => void;
  t: TFunction;
}

function poolMetrics(pool: U3121BillingTecPoolSummary, t: TFunction) {
  return [
    { label: t("lblLineCount", { ns: NS }), value: pool.lineCount },
    { label: t("lblTecTotalHours", { ns: NS }), value: pool.totalHours },
    { label: t("lblTotalAmount", { ns: NS }), value: u3125FormatCurrency(pool.totalAmount) },
    { label: t("lblTotalCostAmount", { ns: NS }), value: u3125FormatCurrency(pool.totalCostAmount) },
  ];
}

function U3121BillingSummaryTecSection({ summary, onOpenDetail, t }: U3121BillingSummaryTecSectionProps) {
  if (summary.tec == null) {
    return (
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-foreground">{t("sectionBillingSummaryTec", { ns: NS })}</h3>
        <U3121BillingSummaryNoAccessPanel messageKey="msgBillingSummaryNoWipAccess" t={t} />
      </section>
    );
  }

  const { uninvoiced, invoiced, writeOff } = summary.tec;
  const showSubProject = summary.includeSubProjet && (uninvoiced.bySubProject?.length ?? 0) > 0;

  return (
    <section className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-foreground">{t("sectionBillingSummaryTec", { ns: NS })}</h3>
        <a
          href={u2000BuildAppRouteHref("/U3134")}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary-600 hover:underline"
        >
          {t("btnGoToWip", { ns: NS })}
        </a>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-3 lg:items-stretch">
        <U3121BillingSummaryMetricCard
          title={t("lblTecUninvoiced", { ns: NS })}
          metrics={poolMetrics(uninvoiced, t)}
          onViewDetail={() => onOpenDetail("uninvoiced")}
          footer={
            <>
              <U3121BillingSummaryKindBreakdown byKind={uninvoiced.byKind} t={t} />
              {showSubProject && uninvoiced.bySubProject ? (
                <U3121BillingSummarySubProjectBreakdown bySubProject={uninvoiced.bySubProject} t={t} />
              ) : null}
            </>
          }
          t={t}
        />
        <U3121BillingSummaryMetricCard
          title={t("lblTecInvoiced", { ns: NS })}
          metrics={poolMetrics(invoiced, t)}
          onViewDetail={() => onOpenDetail("invoiced")}
          footer={<U3121BillingSummaryKindBreakdown byKind={invoiced.byKind} t={t} />}
          t={t}
        />
        <U3121BillingSummaryMetricCard
          title={t("lblTecWriteOff", { ns: NS })}
          metrics={poolMetrics(writeOff, t)}
          onViewDetail={() => onOpenDetail("writeOff")}
          footer={<U3121BillingSummaryKindBreakdown byKind={writeOff.byKind} t={t} />}
          t={t}
        />
      </div>
    </section>
  );
}

export default U3121BillingSummaryTecSection;
